import { format, parseISO } from "date-fns";
import parse from "date-fns/parse";

export const camelToHuman = (str: string): string => {
  const start =
    str.charAt(0).toUpperCase() === str.charAt(0)
      ? ""
      : str.charAt(0).toUpperCase();
  return (
    start +
    str
      .replace(/([A-Z]+)/g, " $1")
      .replace(/([A-Z][a-z])/g, " $1")
      .slice(1)
  );
};

export const formatNumber = (amount: number | string): string => {
  if (!amount && amount !== 0) return "";
  let x: string | number = amount.toString();
  let afterPoint = "";
  if (x.indexOf(".") > 0) afterPoint = x.substring(x.indexOf("."), x.length);
  x = Math.floor(parseFloat(x));
  x = x.toString();
  let lastThree = x.substring(x.length - 3);
  const otherNumbers = x.substring(0, x.length - 3);
  if (otherNumbers !== "") lastThree = "," + lastThree;
  const res =
    otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree + afterPoint;
  return res;
};

export const downloadLink = (link: string) => {
  const a: any = document.createElement("a");
  document.body.appendChild(a);
  a.style = "display: none";
  a.href = link;
  a.download = true;
  a.click();
  document.body.removeChild(a);
};

export const debounce = <T extends (...args: any) => any>(
  func: T,
  delay: number
) => {
  let debounceTimer: NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func(...args), delay);
  };
};

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set#implementing_basic_set_operations
export function isSuperset<T>(set: Set<T>, subset: Set<T>): boolean {
  for (const elem of subset) {
    if (!set.has(elem)) {
      return false;
    }
  }
  return true;
}

export type Values<T extends Record<any, any>> = T[keyof T];

export function objectKeys<T extends object>(obj: T) {
  return Object.keys(obj ?? {}) as Array<keyof T>;
}

export async function copyToClipboard(value: any): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    console.error(error);

    if (value) {
      const selectionInput = document.createElement("textarea");
      selectionInput.value = value;
      document.body.appendChild(selectionInput);
      selectionInput?.focus();
      selectionInput?.select();
      document.execCommand("copy");
      document.body.removeChild(selectionInput);
      return true;
    } else return false;
  }
}

export function formatNumberIntl(
  number: number,
  locale?: string,
  options?: Intl.NumberFormatOptions
): string {
  let result = "";
  const intlOptions = {
    ...(options
      ? options
      : {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 0
        })
  };

  try {
    result = new Intl.NumberFormat(locale || "en-IN", intlOptions).format(
      number
    );
  } catch (ex) {
    console.error(ex);

    result = "";
  }

  return result;
}

export const StringToDate = (dateString: string | null) => {
  if (!dateString) {
    return null;
  }
  return parse(dateString, "yyyy-MM-dd", new Date());
};

export function getFormattedDate(
  dateString: string,
  withTime: boolean = false,
  defaultDate: boolean = false
): string {
  if (!dateString) {
    return "";
  }

  try {
    let inputDateString = dateString;
    if (typeof dateString === "string" && dateString.endsWith(" IST")) {
      inputDateString = dateString.replace(" IST", "+05:30").replace(" ", "T");
    }
    const date = parseISO(inputDateString);

    return !withTime
      ? format(date, "do MMM, yyyy")
      : format(date, "do MMM, yyyy, h:mm aa");
  } catch (ex) {
    console.error(new Error(ex as string).message);
    return defaultDate ? dateString : "";
  }
}

/**
 * Converts UTC datetime string to IST and formats it
 * @param dateString - UTC datetime string in format "YYYY-MM-DD HH:mm:ss" or ISO format
 * @returns Formatted IST datetime string in "YYYY-MM-DD HH:mm:ss" format
 */
export function convertUTCtoIST(dateString: string, ampm?: boolean): string {
  try {
    if (!dateString) {
      return "";
    }

    let utcDate: Date;

    // Look for timezone indicators at the END of the string
    // Patterns: Z, +HH:MM, -HH:MM, +HHMM, -HHMM, +HH, -HH
    const hasTimezone =
      dateString.endsWith("Z") ||
      /[+-]\d{2}:\d{2}$/.test(dateString) || // +05:30 or -08:00
      /[+-]\d{4}$/.test(dateString) || // +0530 or -0800
      /[+-]\d{2}$/.test(dateString); // +00 or -05

    if (hasTimezone) {
      // Replace space with T if needed to make it ISO-compliant
      const isoString = dateString.includes("T")
        ? dateString
        : dateString.replace(" ", "T");

      utcDate = parseISO(isoString);
    } else {
      // No timezone info: backend is sending "YYYY-MM-DD HH:mm:ss" format in UTC
      // Convert to ISO format with Z (UTC indicator)
      utcDate = new Date(dateString.replace(" ", "T") + "Z");
    }

    // Get UTC timestamp and add IST offset (5 hours 30 minutes = 19800000 milliseconds)
    // IST is UTC+05:30
    const istTimestamp = utcDate.getTime() + 5.5 * 60 * 60 * 1000;
    const istDate = new Date(istTimestamp);

    // Format the IST date in UTC context to avoid timezone issues
    const year = istDate.getUTCFullYear();
    const month = String(istDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(istDate.getUTCDate()).padStart(2, "0");
    const hours24 = String(istDate.getUTCHours()).padStart(2, "0");
    const minutes = String(istDate.getUTCMinutes()).padStart(2, "0");
    const seconds = String(istDate.getUTCSeconds()).padStart(2, "0");

    // If ampm param is passed → return AM/PM format
    if (ampm) {
      let h = istDate.getUTCHours();
      const amPmText = h >= 12 ? "PM" : "AM";
      h = h % 12;
      h = h === 0 ? 12 : h;

      const formattedHours = String(h).padStart(2, "0");

      return `${year}-${month}-${day} ${formattedHours}:${minutes} ${amPmText}`;
    }

    // Default old format (24-hour with seconds)
    return `${year}-${month}-${day} ${hours24}:${minutes}:${seconds}`;
  } catch (error) {
    console.error("Error converting UTC to IST:", error, dateString);
    return dateString; // Return original string if conversion fails
  }
}
